import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MessageSquare, Mail, CalendarDays, FileText, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openChat } from "@/lib/chat";

const BOOK_CALL_URL = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2N746MuptRQGLcn_uXzpK2g7lRVFOimO5X-yIKhMlSjfzxexcJvtN7NHVygr-xD2sqD0XLYYLV";
const SUPPORT_EMAIL = "rev@sideb.io";

const CHANNELS = [
  {
    id: "chat",
    icon: MessageSquare,
    eyebrow: "Live chat",
    title: "Talk to us now.",
    body: "Drop us a line in chat. We answer during business hours and pick up async overnight.",
    cta: "Open chat",
    testId: "contact-chat-card",
    btnTestId: "contact-chat-btn",
    onClick: () => openChat({ context: "Contact page · live chat card" }),
  },
  {
    id: "email",
    icon: Mail,
    eyebrow: "Email",
    title: "Send us a note.",
    body: (
      <>
        Reach the team directly at{" "}
        <span className="font-medium text-foreground">{SUPPORT_EMAIL}</span>. Best for quotes, vendor questions, and anything async.
      </>
    ),
    cta: "Email rev@sideb.io",
    testId: "contact-email-card",
    btnTestId: "contact-email-btn",
    href: `mailto:${SUPPORT_EMAIL}`,
  },
  {
    id: "book",
    icon: CalendarDays,
    eyebrow: "Book a call",
    title: "15 minutes, on our calendar.",
    body: "A short intro call to scope a payments diagnostic, AIOps engagement, or studio retainer.",
    cta: "Schedule a call",
    testId: "contact-book-card",
    btnTestId: "contact-book-btn",
    href: BOOK_CALL_URL,
    external: true,
  },
  {
    id: "brief",
    icon: FileText,
    eyebrow: "Custom project",
    title: "Send a project brief.",
    body: "Have a defined scope already? Send the brief and we'll respond within two business days.",
    cta: "Open the brief form",
    testId: "contact-brief-card",
    btnTestId: "contact-brief-btn",
    to: "/inquiry",
  },
];

export default function Contact() {
  return (
    <div className="bg-white" data-testid="contact-page">
      <Helmet>
        <title>Talk to SideB · Operating Partner for Growth-Stage Teams</title>
        <meta name="description" content="Reach SideB. Live chat, email rev@sideb.io, book a 15-minute intro call with an Operating Partner, or submit a custom project brief. Scale your operations without adding headcount." />
        <meta name="keywords" content="Operating Partner, AI Solutions Management, AIOps Partner, Process Improvement Consultant, Scale operations without adding headcount" />
        <meta property="og:title" content="Talk to SideB · Operating Partner for Growth-Stage Teams" />
        <meta property="og:description" content="Live chat, email, 15-min intro call, or custom project brief. SideB partners with growth-stage teams to scale operations without adding headcount." />
        <meta property="og:url" content="https://sideb.io/contact" />
        <meta name="twitter:title" content="Talk to SideB · Operating Partner" />
        <meta name="twitter:description" content="Chat, email, 15-min intro call, or project brief. SideB partners with growth-stage teams." />
      </Helmet>

      {/* HERO */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-5" data-testid="contact-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
            Talk to us
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.035em] leading-[1.02] max-w-3xl">
            Pick the channel that fits.<br />
            <span className="text-[#ac62ea]">We&rsquo;ll meet you there.</span>
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Whether you need a quick answer in chat, a 15-minute intro call on the calendar, or a formal scope sent over &mdash; here&rsquo;s how to reach the team.
          </p>
        </div>
      </section>

      {/* CHANNELS GRID */}
      <section className="bg-[#faf7ff]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid gap-5 sm:grid-cols-2">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.id}
                  className="bg-white border border-border rounded-2xl p-7 lg:p-8 flex flex-col group hover:border-[#ac62ea]/40 hover:shadow-[0_8px_30px_rgb(11,30,90,0.06)] transition-all"
                  data-testid={c.testId}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-[#f3edff] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#ac62ea]" strokeWidth={2} />
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                      {c.eyebrow}
                    </span>
                  </div>
                  <h2 className="font-display text-xl font-semibold tracking-[-0.02em] leading-[1.2]">
                    {c.title}
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-grow">
                    {c.body}
                  </p>
                  <div className="mt-6">
                    {c.onClick ? (
                      <Button
                        onClick={c.onClick}
                        className="rounded-full bg-[#0b1e5a] hover:bg-black h-10 px-5 text-sm font-medium"
                        data-testid={c.btnTestId}
                      >
                        {c.cta}
                        <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Button>
                    ) : c.to ? (
                      <Button asChild className="rounded-full bg-[#0b1e5a] hover:bg-black h-10 px-5 text-sm font-medium" data-testid={c.btnTestId}>
                        <Link to={c.to}>
                          {c.cta}
                          <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      </Button>
                    ) : (
                      <Button asChild className="rounded-full bg-[#0b1e5a] hover:bg-black h-10 px-5 text-sm font-medium" data-testid={c.btnTestId}>
                        <a href={c.href} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined}>
                          {c.cta}
                          <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Office hours band */}
          <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="contact-hours">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Office hours</span> &middot; Mon&ndash;Fri, 8a&ndash;6p PST &middot; async overnight
              </span>
            </div>
            <span className="text-xs font-mono uppercase tracking-[0.14em] text-muted-foreground">
              Response within 1 business day
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
